import { BottomSheetModal } from '@gorhom/bottom-sheet';
import {
  Action,
  ActionType,
  Address,
  ButtonType,
  SuspiciousCardTxnQuestionActionTile,
} from '@varo/graphql-schema-types';
import React, { RefObject } from 'react';
import { StyleSheet, useWindowDimensions, View } from 'react-native';
import { HTMLRenderer } from 'shared/components/HTMLRenderer/HTMLRenderer';
import BottomSheet from 'shared/components/vxl/BottomSheet/BottomSheet';
import Button from 'shared/components/vxl/Button/Button';
import BodyBaseBoldText from 'shared/components/vxl/typography/primary/body/BodyBaseBoldText';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import TitleMdText from 'shared/components/vxl/typography/primary/title/TitleMdText';
import theme from 'shared/styles/theme';

const mapButtonType = (type: ButtonType) => {
  const buttonTypeMap = {
    [ButtonType.Primary]: ButtonType.Primary,
    [ButtonType.Secondary]: ButtonType.Secondary,
    [ButtonType.SecondaryAlt]: ButtonType.SecondaryAlt,
    [ButtonType.Text]: ButtonType.Text,
    [ButtonType.Skip]: ButtonType.Secondary,
    [ButtonType.Wallet]: ButtonType.Primary,
    [ButtonType.CloseNavigation]: ButtonType.Secondary,
    [ButtonType.UnknownButtonType]: ButtonType.Primary,
  };

  return (buttonTypeMap[type] || ButtonType.Primary) as
    | ButtonType.Primary
    | ButtonType.Secondary
    | ButtonType.SecondaryAlt
    | ButtonType.Text
    | undefined;
};

const SuspiciousTransactionDeclinedBottomSheet = ({
  data,
  suspiciousTransactionDeclinedBottomSheetRef,
  onPressCtaAction,
  updatingTransactionStatus,
  replaceCardFetching,
}: {
  data?: SuspiciousCardTxnQuestionActionTile;
  suspiciousTransactionDeclinedBottomSheetRef: RefObject<BottomSheetModal | null>;
  onPressCtaAction: (cta: Action) => void;
  updatingTransactionStatus: boolean;
  replaceCardFetching: boolean;
}) => {
  const { width } = useWindowDimensions();

  const getAddressText = (address: Address): string => {
    return address
      ? `${address.addressLine1}, ${address.addressLine2 ?? ''} \n ${address.city}, ${address.state} ${address.zip}`
      : '';
  };

  return (
    <BottomSheet ref={suspiciousTransactionDeclinedBottomSheetRef}>
      <View style={styles.container}>
        {data?.title ? (
          <View style={styles.titlePadding}>
            <TitleMdText numberOfLines={0}>{data.title}</TitleMdText>
          </View>
        ) : null}
        {data?.message ? (
          <BodyBaseText numberOfLines={5}>{data.message}</BodyBaseText>
        ) : null}
        {data?.html ? (
          <HTMLRenderer
            contentWidth={width}
            htmlAsString={data?.html}
            tagsStyles={htmlStyles}
          />
        ) : null}
        <View style={styles.addressSpace}>
          <View style={styles.addressSpace}>
            {data?.addressSectionTitle ? (
              <View style={styles.titlePadding}>
                <TitleMdText numberOfLines={0}>
                  {data.addressSectionTitle}
                </TitleMdText>
              </View>
            ) : null}
            {data?.addressSectionSubTitle ? (
              <BodyBaseText numberOfLines={0}>
                {data.addressSectionSubTitle}
              </BodyBaseText>
            ) : null}
          </View>
          <View>
            <BodyBaseBoldText numberOfLines={0}>
              {data?.addressTitle}
            </BodyBaseBoldText>
            {data?.mailingAddress ? (
              <BodyBaseText numberOfLines={0}>
                {getAddressText(data.mailingAddress)}
              </BodyBaseText>
            ) : null}
          </View>
        </View>

        {data?.ctaActions?.map((cta: Action) => {
          return (
            <View key={`action-${cta.id}`}>
              <View style={styles.buttonSpace} />
              <Button
                type={mapButtonType(cta?.type)}
                key={cta.id}
                label={cta.copy}
                isLoading={
                  cta.action === ActionType.CancelSuspiciousTransaction &&
                  (updatingTransactionStatus ? true : replaceCardFetching)
                }
                onPress={() => onPressCtaAction(cta)}
                analyticsId={
                  cta.action === ActionType.CancelSuspiciousTransaction
                    ? 'transaction_declined_cancel'
                    : 'transaction_declined_dont_cancel'
                }
                customAttributes={{
                  cta: cta.copy,
                }}
              />
            </View>
          );
        })}
      </View>
    </BottomSheet>
  );
};

const styles = StyleSheet.create({
  addressSpace: {
    paddingVertical: theme.spacing.m,
  },
  buttonSpace: {
    paddingTop: theme.spacing.m,
  },
  container: {
    paddingBottom: theme.spacing.m,
    paddingHorizontal: theme.spacing.m,
  },
  titlePadding: {
    paddingBottom: theme.spacing.s,
  },
});

const htmlStyles = StyleSheet.create({
  // Disabled ESLint for unused styles in this file, as they're passed to the HTMLRenderer component.
  // eslint-disable-next-line react-native/no-unused-styles
  li: {
    marginBottom: theme.spacing.xs,
    paddingLeft: theme.spacing.xs,
  },
  // eslint-disable-next-line react-native/no-unused-styles
  ul: {
    paddingLeft: theme.spacing.m,
  },
});

export default SuspiciousTransactionDeclinedBottomSheet;
